import { LOGO_HERO_URL } from "../lib/brand";
import { useTrackRecord } from "../hooks/useTrackRecord";
import { useT } from "../i18n/LanguageContext";
import { usePageSEO } from "../hooks/usePageSEO";
import { track } from "../lib/analytics";
import { ArrowRight, ShieldCheck, Activity, Sparkles, LineChart } from "lucide-react";

const SALE_ACTIVE = Date.now() < new Date("2026-10-31T23:59:59+01:00").getTime();

export const Hero = () => {
  const { t, lang } = useT();
  usePageSEO({
    title:
      lang === "de"
        ? "Alphalane Trading Systems — KI-Trading Schweiz | Automatisiert Geld verdienen"
        : "Alphalane Trading Systems — AI Trading Switzerland | Earn money automatically",
    description:
      lang === "de"
        ? "Alphalane Trading Systems – KI-gestütztes, vollautomatisiertes Trading aus der Schweiz. Profitiere vom Markt ohne selbst zu handeln. Performance-Fee 40%, Mindesteinlage 2'000$, transparenter Track Record."
        : "Alphalane Trading Systems – AI-driven, fully automated trading from Switzerland. Profit from the markets without trading yourself. 40% performance fee, 2,000$ minimum deposit, transparent track record.",
    canonical: "https://alphalane-tradingsystems.com/",
  });
  const { rows } = useTrackRecord();
  const cumul = rows.length ? rows[rows.length - 1].cumulative : 0;
  const cumulText = `${cumul > 0 ? "+" : ""}${cumul.toFixed(2)}%`;
  const barWidth = Math.min(95, Math.max(10, (Math.abs(cumul) / 500) * 100));

  // Derive the "since X" label from the first row of the track record.
  // Accepts "Mai 2025", "May 2025", "2025-05", etc — falls back to "—".
  const firstMonthLabel = (() => {
    const first = rows.find((r) => !r.paused) || rows[0];
    if (!first || !first.month) return "—";
    const raw = String(first.month).trim();
    // German short month names
    const deShort = { Januar: "Jan", Februar: "Feb", März: "März", April: "Apr",
                      Mai: "Mai", Juni: "Jun", Juli: "Jul", August: "Aug",
                      September: "Sep", Oktober: "Okt", November: "Nov", Dezember: "Dez" };
    const enShort = { January: "Jan", February: "Feb", March: "Mar", April: "Apr",
                      May: "May", June: "Jun", July: "Jul", August: "Aug",
                      September: "Sep", October: "Oct", November: "Nov", December: "Dec" };
    const map = lang === "de" ? deShort : enShort;
    // Try "Month Year" pattern (e.g. "Mai 2025")
    const m = raw.match(/^(\S+)\s+(\d{4})$/);
    if (m) {
      const monthAbbr = map[m[1]] || m[1].slice(0, 3);
      return `${monthAbbr} '${m[2].slice(2)}`;
    }
    return raw;
  })();
  const cumulSinceLabel = (t.hero.cumulSinceTpl || "{month}").replace("{month}", firstMonthLabel);

  return (
    <section id="top" data-testid="hero-section" className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
      <div className="al-blob" style={{ top: "-120px", left: "-120px", width: 420, height: 420, background: "radial-gradient(closest-side, rgba(74,143,231,0.45), transparent)" }} />
      <div className="al-blob" style={{ top: "60px", right: "-160px", width: 520, height: 520, background: "radial-gradient(closest-side, rgba(168,85,247,0.40), transparent)" }} />
      <div className="absolute inset-0 al-grid-bg opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 al-rise al-d-0">
          <span data-testid="hero-tag" className="al-tag">
            <Sparkles className="h-3.5 w-3.5" /> {t.hero.tag}
          </span>
          <h1 data-testid="hero-headline" className="mt-6 font-display font-bold text-[44px] sm:text-6xl lg:text-7xl leading-[1.02]">
            {t.hero.h1a}<br className="hidden sm:block" />
            <span className="al-grad-text">{t.hero.h1b}</span>
          </h1>
          <p data-testid="hero-sub" className="mt-7 text-lg text-slate-600 max-w-2xl leading-relaxed">{t.hero.sub}</p>

          <div className="mt-9 flex flex-wrap gap-3">
            <a href="#kontakt" data-testid="hero-cta-primary" onClick={() => track("cta_click", { meta: { id: "hero_primary" } })} className="al-cta">
              {t.hero.ctaPrimary} <ArrowRight className="h-4 w-4" />
            </a>
            <a href="#konzept" data-testid="hero-cta-secondary" onClick={() => track("cta_click", { meta: { id: "hero_secondary" } })} className="al-cta-ghost">
              {t.hero.ctaSecondary}
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-5 max-w-xl">
            <Stat value="24/5" label={t.hero.stat1} />
            {SALE_ACTIVE ? (
              <div data-testid="hero-stat-performance-fee-sale">
                <div className="font-display font-bold text-2xl sm:text-3xl">
                  <span className="text-slate-400 line-through text-lg sm:text-xl mr-1.5">40%</span>
                  <span style={{ color: "#9a3412" }}>20%</span>
                </div>
                <div className="mt-1 text-xs sm:text-sm text-slate-500 uppercase tracking-wider font-medium">
                  {t.hero.stat2} <span className="font-bold" style={{ color: "#9a3412" }}>{lang === "de" ? "· Herbst-Sale" : "· Autumn Sale"}</span>
                </div>
              </div>
            ) : (
              <Stat value="40%" label={t.hero.stat2} />
            )}
            <Stat value="2'000$" label={t.hero.stat3} />
          </div>
        </div>

        <div className="lg:col-span-5 al-rise al-d-2">
          <div className="relative">
            <div className="absolute inset-0 al-grad-bg rounded-[28px] blur-3xl opacity-25" />
            <div className="relative al-card p-8 sm:p-10 rounded-[28px]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">{t.hero.liveStatus}</span>
                </div>
                <span className="text-xs font-mono-tab text-slate-400">v2 · MT5</span>
              </div>

              <div className="mt-6 flex items-center justify-center">
                <img src={LOGO_HERO_URL} alt="Alphalane AI" className="h-32 w-32 object-contain" />
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <Mini icon={<Activity className="h-4 w-4" />} label={t.hero.strategy} value={t.hero.strategyVal} />
                <Mini icon={<ShieldCheck className="h-4 w-4" />} label={t.hero.drawdown} value={t.hero.drawdownVal} />
              </div>

              <div className="mt-6 rounded-2xl border border-[var(--al-line)] p-4 bg-[var(--al-bg)]">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{cumulSinceLabel}</span>
                  <span className="font-display font-bold text-2xl al-grad-text" data-testid="hero-cumulative">{cumulText}</span>
                </div>
                <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full al-grad-bg" style={{ width: `${barWidth}%` }} />
                </div>
                <p className="mt-2 text-[11px] text-slate-400">{t.hero.historicalNote}</p>
                <a
                  href="#track-record"
                  data-testid="hero-trackrecord-cta"
                  onClick={() => track("hero_trackrecord_cta_click")}
                  className="mt-4 group inline-flex w-full items-center justify-center gap-2 rounded-full border border-[var(--al-line)] bg-white px-4 py-2.5 text-sm font-display font-bold text-slate-800 hover:border-violet-300 hover:bg-violet-50 transition"
                >
                  <LineChart className="h-4 w-4 al-grad-text-icon" strokeWidth={2.4} />
                  <span className="al-grad-text">{lang === "de" ? "Vollständigen Track Record ansehen" : "View full track record"}</span>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-violet-600 group-hover:translate-x-0.5 transition" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ value, label }) => (
  <div data-testid={`hero-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="font-display font-bold text-2xl sm:text-3xl al-grad-text">{value}</div>
    <div className="mt-1 text-xs sm:text-sm text-slate-500 uppercase tracking-wider font-medium">{label}</div>
  </div>
);

const Mini = ({ icon, label, value }) => (
  <div className="rounded-xl border border-[var(--al-line)] p-3.5 flex flex-col items-center justify-center text-center min-h-[92px]">
    <div className="flex items-center justify-center gap-1.5 text-slate-500 text-[11px] uppercase tracking-widest font-semibold leading-tight">
      <span className="hidden sm:inline-flex">{icon}</span>
      <span>{label}</span>
    </div>
    <div className="mt-2 font-display font-semibold text-slate-900 text-sm">{value}</div>
  </div>
);
